import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Loader from 'cccisd-loader';
import Tooltip from 'cccisd-tooltip';
import { useQuery } from 'cccisd-react-query';
import { client } from 'cccisd-apollo';
import AssessmentCard from './Card';
import useInfiniteScrollAssessments from 'js/hooks/useInfiniteScrollAssessments';
import _intersection from 'lodash/intersection';
import progressQuery from '../progress.graphql';
import style from './style.css';
// icons
import IconEnlarge from 'cccisd-icons/enlarge7';
import IconShrink from 'cccisd-icons/shrink7';
import IconPlus from 'cccisd-icons/plus-circle2';
import ArrowUp from 'cccisd-icons/arrow-up7';
import ArrowDown from 'cccisd-icons/arrow-down7';

const AssessmentCards = props => {
    const {
        selectedStudents,
        forceReloadQueries,
        setForceReloadQueries,
        handleAssignAssessments,
        filters,
        thereAreFiltersSet,
        onLoadAllAssessmentsReady,
    } = props;
    const [collapsed, setCollapsed] = useState([]);
    const [planList, setPlanList] = useState([]);
    const [isDescendingSort, setisDescendingSort] = useState(true);
    const [orderBy, setOrderBy] = useState([{ field: 'createdAt', direction: 'DESC' }]);
    const { sessionIds, pawnIds } = useMemo(() => {
        const sessionIdSet = new Set();
        const pawnIdSet = new Set();

        planList.forEach(plan => {
            plan.sessionList?.forEach(session => {
                if (session.sessionId) {
                    sessionIdSet.add(session.sessionId);
                }
                if (session.sessionDeploymentInfo) {
                    Object.keys(session.sessionDeploymentInfo).forEach(pawnId => {
                        const parsedPawnId = Number(pawnId);
                        if (!Number.isNaN(parsedPawnId)) {
                            pawnIdSet.add(parsedPawnId);
                        }
                    });
                }
            });
        });

        return {
            sessionIds: [...sessionIdSet],
            pawnIds: [...pawnIdSet],
        };
    }, [planList]);

    // Create filter for selectedStudents
    const studentsFilter =
        selectedStudents.length > 0
            ? { in: { field: 'selectedByPawn.pawn.pawnId', int: selectedStudents.map(s => s.pawnId) } }
            : null;

    const {
        assessmentPlans: infiniteScrollPlans,
        // loading: infiniteLoading,
        error: infiniteError,
        hasMore,
        isLoadingMore,
        lastElementRef,
        loadAllAssessments,
    } = useInfiniteScrollAssessments(forceReloadQueries, orderBy, studentsFilter, thereAreFiltersSet);

    useEffect(() => {
        if (onLoadAllAssessmentsReady) {
            onLoadAllAssessmentsReady(loadAllAssessments);
        }
    }, [onLoadAllAssessmentsReady, loadAllAssessments]);

    useEffect(() => {
        if (infiniteScrollPlans.length > 0) {
            const initialCollapsed = infiniteScrollPlans
                .filter(plan => {
                    const planSettings = JSON.parse(plan.settings);
                    return !!planSettings.stopAssessing || !!plan.completedDate;
                })
                .map(plan => plan.assignmentPlanId);
            setCollapsed(initialCollapsed);
        }
    }, [infiniteScrollPlans]);

    const { data: progressData, isLoading: progressLoading } = useQuery({
        queryKey: ['assessmentCardsProgress', sessionIds, pawnIds],
        queryFn: async () => {
            const res = await client.query({
                query: progressQuery,
                variables: {
                    sessionIds,
                    pawnIds,
                    filter: null,
                },
                fetchPolicy: 'network-only',
            });

            return res.data.flows;
        },
        enabled: sessionIds.length > 0 && pawnIds.length > 0,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        setPlanList(filterAssessmentPlans());
    }, [infiniteScrollPlans, filters, orderBy, hasMore]);

    // Trigger new query when selectedStudents changes
    useEffect(() => {
        setForceReloadQueries(prev => prev + 1);
    }, [selectedStudents, setForceReloadQueries]);

    const filterAssessmentPlans = () => {
        let plans = [...infiniteScrollPlans];

        if (filters?.archived === 'show') {
            plans = plans.filter(plan => plan.archivedDate);
        } else if (filters?.archived === 'hide') {
            plans = plans.filter(plan => !plan.archivedDate);
        }

        plans = plans.filter(plan => {
            const planSettings = JSON.parse(plan.settings);
            const planStopped = planSettings.stopAssessing && planSettings.stopAssessing;
            const matchesCompleted = filters?.completed && !!plan.completedDate;
            const matchesReady = filters?.ready && !plan.completedDate && !planStopped;
            const matchesStopped = filters?.stopped && planStopped;

            // If all progress checkbox filters are unselected (default state (false)), dont enforce any progress filter
            // If any progress filter is selected, enforce the filters
            const matchProgress =
                filters?.completed || filters?.ready || filters?.stopped
                    ? matchesCompleted || matchesReady || matchesStopped
                    : true;

            const matchesAssessment =
                [...filters.assessments].length === 0
                    ? true
                    : _intersection(plan.sessionList.map(s => s.settings.resourceIds).flat(), [...filters.assessments])
                          .length > 0;

            return matchesAssessment && matchProgress;
        });

        // Once all plans are loaded, sort in-memory so we avoid an unnecessary refetch.
        if (!hasMore && orderBy?.length > 0) {
            const [{ field, direction }] = orderBy;
            const multiplier = direction === 'DESC' ? -1 : 1;

            plans.sort((a, b) => {
                const aValue = a?.[field];
                const bValue = b?.[field];

                const aTime = Date.parse(aValue);
                const bTime = Date.parse(bValue);
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
                    return (aTime - bTime) * multiplier;
                }

                if (aValue === bValue) return 0;
                return aValue > bValue ? multiplier : -multiplier;
            });
        }

        return plans;
    };

    if (infiniteError) {
        return <div className="alert alert-danger">Error loading assessments</div>;
    }

    const renderCards = () => {
        const buttonContent = (
            <div className="d-flex justify-content-between mb-3">
                <Tooltip
                    title={
                        selectedStudents.length === 1
                            ? `Select assessments for ${selectedStudents[0].name}.`
                            : 'Select assessments for a student.'
                    }
                >
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleAssignAssessments}
                        data-assign-assessments
                    >
                        <IconPlus /> Assign Assessments{' '}
                        {selectedStudents.length === 1 ? `for ${selectedStudents[0].name}` : ''}
                    </button>
                </Tooltip>
                {(planList.length > 0 || infiniteScrollPlans.length > 0) && (
                    <div>
                        <Tooltip title="Expand all.">
                            <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={() => {
                                    setCollapsed([]);
                                }}
                            >
                                <IconEnlarge />
                            </button>
                        </Tooltip>
                        <Tooltip title="Collapse all.">
                            <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={() => {
                                    setCollapsed(planList.map(plan => plan.assignmentPlanId));
                                }}
                            >
                                <IconShrink />
                            </button>
                        </Tooltip>
                        <Tooltip title={`Sort by date - ${isDescendingSort ? 'oldest first' : 'newest first'}.`}>
                            <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={() => {
                                    const newDirection = isDescendingSort ? 'ASC' : 'DESC';
                                    setOrderBy([{ field: 'createdAt', direction: newDirection }]);
                                    setisDescendingSort(!isDescendingSort);

                                    if (hasMore) {
                                        setForceReloadQueries(prev => prev + 1);
                                    }
                                }}
                            >
                                {isDescendingSort ? <ArrowUp /> : <ArrowDown />}
                            </button>
                        </Tooltip>
                    </div>
                )}
            </div>
        );

        if (infiniteScrollPlans.length === 0) {
            return (
                <>
                    <div>You have no assigned assessments. Click on Assign Assessments to get started.</div>
                    {buttonContent}
                </>
            );
        }

        if (planList.length === 0) {
            if (filters?.archived === 'show') {
                return (
                    <>
                        {buttonContent}
                        <div>No archived assessments.</div>
                    </>
                );
            }
            return (
                <>
                    {buttonContent}
                    <div>No assessments match the selected filters.</div>
                </>
            );
        }

        return (
            <>
                {buttonContent}
                {filters?.archived === 'show' && <h4>Archived ({planList.length})</h4>}
                {planList.map((plan, index) => (
                    <div key={plan.assignmentPlanId}>
                        <AssessmentCard
                            plan={plan}
                            progressList={progressData?.assignmentProgressList || []}
                            progressLoading={progressLoading}
                            collapsed={collapsed}
                            setCollapsed={setCollapsed}
                            setForceReloadQueries={setForceReloadQueries}
                            filters={filters}
                        />
                        {index === planList.length - 1 && <div ref={lastElementRef} style={{ height: '1px' }} />}
                    </div>
                ))}
                {isLoadingMore && (
                    <div className="d-flex justify-content-center my-3">
                        <Loader loading />
                    </div>
                )}
                {!hasMore && planList.length > 9 && (
                    <div className="text-center text-muted my-3">No more assessments to load</div>
                )}
            </>
        );
    };

    return <div className={style.assessmentCards}>{renderCards()}</div>;
};

export default AssessmentCards;

AssessmentCards.propTypes = {
    selectedStudents: PropTypes.array,
    forceReloadQueries: PropTypes.number,
    setForceReloadQueries: PropTypes.func,
    filters: PropTypes.shape({
        ready: PropTypes.bool,
        stopped: PropTypes.bool,
        completed: PropTypes.bool,
        assessments: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        archived: PropTypes.string,
    }),
    handleAssignAssessments: PropTypes.func,
    thereAreFiltersSet: PropTypes.bool,
    onLoadAllAssessmentsReady: PropTypes.func,
};
AssessmentCards.defaultProps = {};
